import React from 'react';
import { Calendar, MapPin, BookOpen, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { BookClubSettings } from '@/types/chat';
import { format } from 'date-fns';

interface BookClubInfoBannerProps {
  settings: BookClubSettings;
  currentBook?: {
    title: string;
    cover_url?: string;
  } | null;
  onClick?: () => void;
}

const formatLabels: Record<string, string> = {
  virtual: 'Virtual',
  in_person: 'In Person',
  hybrid: 'Hybrid',
};

export const BookClubInfoBanner: React.FC<BookClubInfoBannerProps> = ({
  settings,
  currentBook,
  onClick,
}) => {
  return (
    <div
      className="border-b bg-muted/30 px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors"
      onClick={onClick}
    >
      <div className="flex items-center gap-4 overflow-x-auto">
        {/* Current book */}
        {currentBook && (
          <div className="flex items-center gap-2 shrink-0">
            {currentBook.cover_url ? (
              <img
                src={currentBook.cover_url}
                alt={currentBook.title}
                className="w-8 h-11 rounded object-cover"
              />
            ) : (
              <BookOpen className="w-4 h-4 text-muted-foreground" />
            )}
            <span className="text-sm font-medium truncate max-w-[140px]">
              {currentBook.title}
            </span>
          </div>
        )}

        {/* Next meeting */}
        {settings.next_meeting_at && (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground shrink-0">
            <Calendar className="w-3.5 h-3.5" />
            <span>{format(new Date(settings.next_meeting_at), 'EEE d MMM')}</span>
            <Badge variant="outline" className="text-xs px-1.5 py-0">
              {formatLabels[settings.meeting_format] || settings.meeting_format}
            </Badge>
          </div>
        )}

        {/* Reading pace */}
        {settings.reading_pace && (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground shrink-0">
            <Clock className="w-3.5 h-3.5" />
            <span>{settings.reading_pace}</span>
          </div>
        )}

        {/* Location */}
        {settings.meeting_location && (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground shrink-0">
            <MapPin className="w-3.5 h-3.5" />
            <span className="truncate max-w-[120px]">{settings.meeting_location}</span>
          </div>
        )}
      </div>
    </div>
  );
};
